import JsSIP from "jssip";

const WSS_URL = "wss://pbx.rds.net.pk:7443";
const TURN_REFRESH_MARGIN_MS = 5 * 60 * 1000;
const TENANTS = {
  redrackx: {
    name: "RedRackX",
    realm: "redrackx.pbx.rds.net.pk",
    logo: "/assets/redrackx-logo.png",
    bodyClass: "theme-redrackx",
    accent: "#ed1c24",
  },
  lokridge: {
    name: "Lokridge",
    realm: "lokridge.pbx.rds.net.pk",
    logo: "/assets/lokridge-logo.png",
    bodyClass: "theme-lokridge",
    accent: "#ec2028",
  },
};

const hostTenant = window.location.hostname.toLowerCase().startsWith("lokridge.")
  ? "lokridge"
  : window.location.hostname.toLowerCase().startsWith("redrackx.")
    ? "redrackx"
    : new URLSearchParams(window.location.search).get("tenant") === "lokridge"
      ? "lokridge"
      : "redrackx";
const tenant = TENANTS[hostTenant];

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];
const els = {
  brandLogo: $("#brandLogo"),
  dialogLogo: $("#dialogLogo"),
  portalTitle: $("#portalTitle"),
  registrationStatus: $("#registrationStatus"),
  registrationLabel: $("#registrationLabel"),
  accountButton: $("#accountButton"),
  accountDialog: $("#accountDialog"),
  accountForm: $("#accountForm"),
  extensionInput: $("#extensionInput"),
  passwordInput: $("#passwordInput"),
  rememberExtension: $("#rememberExtension"),
  accountError: $("#accountError"),
  connectButton: $("#connectButton"),
  disconnectButton: $("#disconnectButton"),
  dialogRealm: $("#dialogRealm"),
  avatar: $("#avatar"),
  agentIdentity: $("#agentIdentity"),
  tenantRealm: $("#tenantRealm"),
  availabilityText: $("#availabilityText"),
  dndButton: $("#dndButton"),
  teamContacts: $("#teamContacts"),
  callState: $("#callState"),
  callTimer: $("#callTimer"),
  dialNumber: $("#dialNumber"),
  backspaceButton: $("#backspaceButton"),
  callButton: $("#callButton"),
  hangupButton: $("#hangupButton"),
  muteButton: $("#muteButton"),
  holdButton: $("#holdButton"),
  transferButton: $("#transferButton"),
  transferDialog: $("#transferDialog"),
  transferForm: $("#transferForm"),
  transferTarget: $("#transferTarget"),
  transferError: $("#transferError"),
  incomingDialog: $("#incomingDialog"),
  incomingCaller: $("#incomingCaller"),
  incomingNumber: $("#incomingNumber"),
  answerButton: $("#answerButton"),
  rejectButton: $("#rejectButton"),
  activityList: $("#activityList"),
  clearHistoryButton: $("#clearHistoryButton"),
  remoteAudio: $("#remoteAudio"),
};

let ua = null;
let activeSession = null;
let pendingIncomingSession = null;
let connectedExtension = "";
let dndEnabled = false;
let muted = false;
let held = false;
let timerHandle = null;
let callStartedAt = null;
let activity = [];
let audioContext = null;
let toneInterval = null;
let activeToneNodes = new Set();
let remoteStream = null;
let webRtcPcConfig = null;
let turnConfigExpiresAt = 0;
let connectedPassword = "";
const attachedPeerConnections = new WeakSet();

async function requestWebRtcPcConfig(extension, password) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  let response;
  try {
    response = await fetch("/api/turn-credentials", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
      body: JSON.stringify({ extension, password }),
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error("The secure media service timed out. Please reconnect.");
    }
    throw new Error("The secure media service is unavailable. Please reconnect.");
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 401) {
    throw new Error("Extension or SIP password is incorrect.");
  }
  if (!response.ok) {
    throw new Error("The secure media service could not prepare this phone.");
  }

  const payload = await response.json();
  if (!Array.isArray(payload.iceServers) || !payload.iceServers.length || !Number.isFinite(payload.expiresAt)) {
    throw new Error("The secure media service returned an invalid configuration.");
  }
  turnConfigExpiresAt = payload.expiresAt * 1000;
  webRtcPcConfig = {
    iceServers: payload.iceServers,
    // All browser media is relayed by the PBX-side TURN service in Pakistan.
    // This prevents private-only SDP from being accepted as a connected call.
    iceTransportPolicy: "relay",
    iceCandidatePoolSize: 0,
  };
  return webRtcPcConfig;
}

async function ensureWebRtcPcConfig(extension = connectedExtension, password = connectedPassword) {
  if (webRtcPcConfig && turnConfigExpiresAt - Date.now() > TURN_REFRESH_MARGIN_MS) {
    return webRtcPcConfig;
  }
  if (!extension || !password) {
    throw new Error("Reconnect the extension to refresh secure audio access.");
  }
  return requestWebRtcPcConfig(extension, password);
}

function initBrand() {
  document.body.classList.add(tenant.bodyClass);
  document.title = `${tenant.name} Secure Web Phone`;
  document.querySelector('meta[name="theme-color"]').setAttribute("content", tenant.accent);
  els.brandLogo.src = tenant.logo;
  els.dialogLogo.src = tenant.logo;
  els.brandLogo.alt = `${tenant.name} logo`;
  els.dialogLogo.alt = `${tenant.name} logo`;
  els.portalTitle.textContent = `${tenant.name} Web Phone`;
  els.dialogRealm.textContent = tenant.realm;
  els.tenantRealm.textContent = tenant.realm;

  const storedExtension = localStorage.getItem(`rds-phone:${hostTenant}:extension`);
  if (storedExtension) els.extensionInput.value = storedExtension;
  renderContacts();
}

function renderContacts() {
  els.teamContacts.replaceChildren();
  ["1011", "1012"].forEach((extension) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "contact-row";
    button.innerHTML = `<span class="contact-avatar">${extension.slice(-2)}</span><span><strong>Agent ${extension}</strong><small>Extension ${extension}</small></span><span class="contact-call" aria-hidden="true">↗</span>`;
    button.addEventListener("click", () => {
      els.dialNumber.value = extension;
      updateCallButton();
      els.dialNumber.focus();
    });
    els.teamContacts.append(button);
  });
}

function setRegistration(state, label) {
  els.registrationStatus.className = `status-pill ${state}`;
  els.registrationLabel.textContent = label;
  els.availabilityText.textContent = state === "online" ? (dndEnabled ? "Do not disturb" : "Available") : label;
  els.callState.textContent = state === "online" ? "Ready to call" : label;
  updateCallButton();
}

function setConnectedIdentity(extension) {
  connectedExtension = extension;
  els.agentIdentity.textContent = extension ? `Extension ${extension}` : "No extension";
  els.avatar.textContent = extension ? extension.slice(-2) : "--";
  els.tenantRealm.textContent = extension ? tenant.realm : "Account not connected";
  els.disconnectButton.classList.toggle("hidden", !extension);
}

function sanitizedNumber(value) {
  return value.trim().replace(/[^0-9*#+]/g, "");
}

function getAudioContext() {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) audioContext = new AudioContextClass();
  }
  return audioContext;
}

async function unlockAudio() {
  const context = getAudioContext();
  if (context?.state === "suspended") await context.resume();
}

function playToneBurst(frequencies, durationMs, delayMs = 0) {
  const context = getAudioContext();
  if (!context || context.state !== "running") return;
  const start = context.currentTime + (delayMs / 1000);
  const stop = start + (durationMs / 1000);
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.045, start + 0.025);
  gain.gain.setValueAtTime(0.045, Math.max(start + 0.03, stop - 0.04));
  gain.gain.exponentialRampToValueAtTime(0.0001, stop);
  gain.connect(context.destination);

  frequencies.forEach((frequency) => {
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.connect(gain);
    activeToneNodes.add(oscillator);
    oscillator.addEventListener("ended", () => activeToneNodes.delete(oscillator), { once: true });
    oscillator.start(start);
    oscillator.stop(stop + 0.02);
  });
}

function stopCallTone() {
  clearInterval(toneInterval);
  toneInterval = null;
  activeToneNodes.forEach((node) => {
    try { node.stop(); } catch (_) { /* oscillator has already stopped */ }
  });
  activeToneNodes.clear();
}

function startCallTone(kind) {
  stopCallTone();
  const playPattern = () => {
    if (kind === "incoming") {
      playToneBurst([440, 480], 400);
      playToneBurst([440, 480], 400, 600);
    } else {
      playToneBurst([440, 480], 1600);
    }
  };
  playPattern();
  toneInterval = setInterval(playPattern, kind === "incoming" ? 4000 : 5000);
}

async function checkMicrophone() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("This browser does not provide microphone access.");
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  stream.getTracks().forEach((track) => track.stop());
}

function mediaErrorMessage(error) {
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return "Microphone access is blocked. Allow the microphone for this site, then try again.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No microphone was found on this device.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "The microphone is in use by another application.";
  }
  return error instanceof Error ? error.message : "The browser could not start audio.";
}

function resetRemoteAudio() {
  if (remoteStream) remoteStream.getTracks().forEach((track) => remoteStream.removeTrack(track));
  remoteStream = null;
  els.remoteAudio.srcObject = null;
}

async function playRemoteAudio() {
  els.remoteAudio.muted = false;
  els.remoteAudio.volume = 1;
  try {
    await els.remoteAudio.play();
  } catch (_) {
    els.callState.textContent = "Call connected — click the page to enable sound";
    document.addEventListener("click", () => els.remoteAudio.play().catch(() => {}), { once: true });
  }
}

function setRemoteAudioStream(stream) {
  if (!stream?.getAudioTracks().length) return;
  remoteStream = stream;
  if (els.remoteAudio.srcObject !== stream) els.remoteAudio.srcObject = stream;
  playRemoteAudio();
}

function syncRemoteAudio(peerconnection) {
  const tracks = peerconnection?.getReceivers?.()
    .map((receiver) => receiver.track)
    .filter((track) => track?.kind === "audio" && track.readyState === "live") || [];
  if (!tracks.length) return;
  const stream = remoteStream || new MediaStream();
  tracks.forEach((track) => {
    if (!stream.getTracks().includes(track)) stream.addTrack(track);
  });
  setRemoteAudioStream(stream);
}

function attachPeerConnection(session, peerconnection) {
  if (!peerconnection || attachedPeerConnections.has(peerconnection)) return;
  attachedPeerConnections.add(peerconnection);
  peerconnection.addEventListener("track", (event) => {
    const [eventStream] = event.streams;
    if (eventStream) {
      setRemoteAudioStream(eventStream);
    } else {
      const stream = remoteStream || new MediaStream();
      if (!stream.getTracks().includes(event.track)) stream.addTrack(event.track);
      setRemoteAudioStream(stream);
    }
  });
  peerconnection.addEventListener("iceconnectionstatechange", () => {
    if (["failed", "disconnected"].includes(peerconnection.iceConnectionState) && activeSession === session) {
      els.callState.textContent = "Call connected — audio network interrupted";
    }
  });
  peerconnection.addEventListener("connectionstatechange", () => {
    if (peerconnection.connectionState === "connected") syncRemoteAudio(peerconnection);
  });
  syncRemoteAudio(peerconnection);
}

function updateCallButton() {
  const registered = Boolean(ua && ua.isRegistered());
  els.callButton.disabled = !registered || !sanitizedNumber(els.dialNumber.value) || Boolean(activeSession);
}

function showDialog(dialog) {
  if (!dialog.open) dialog.showModal();
}

function closeDialog(dialog) {
  if (dialog.open) dialog.close();
}

async function connectAccount(extension, password) {
  disconnectAccount(false);
  els.accountError.textContent = "";
  els.connectButton.disabled = true;
  els.connectButton.textContent = "Connecting…";
  setRegistration("connecting", "Connecting");

  try {
    await requestWebRtcPcConfig(extension, password);
    connectedPassword = password;
    const socket = new JsSIP.WebSocketInterface(WSS_URL);
    ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${extension}@${tenant.realm}`,
      authorization_user: extension,
      password,
      display_name: `${tenant.name} ${extension}`,
      register: true,
      register_expires: 180,
      session_timers: true,
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 30,
    });

    ua.on("connected", () => setRegistration("connecting", "Registering"));
    ua.on("disconnected", () => setRegistration("offline", "Disconnected"));
    ua.on("registered", () => {
      setConnectedIdentity(extension);
      setRegistration("online", "Connected");
      els.passwordInput.value = "";
      els.connectButton.disabled = false;
      els.connectButton.textContent = "Reconnect";
      if (els.rememberExtension.checked) {
        localStorage.setItem(`rds-phone:${hostTenant}:extension`, extension);
      } else {
        localStorage.removeItem(`rds-phone:${hostTenant}:extension`);
      }
      closeDialog(els.accountDialog);
    });
    ua.on("unregistered", () => setRegistration("offline", "Not registered"));
    ua.on("registrationFailed", (event) => {
      const cause = event?.cause || "Registration failed";
      els.accountError.textContent = cause === "Rejected" ? "Extension or SIP password is incorrect." : `Could not register: ${cause}`;
      connectedPassword = "";
      webRtcPcConfig = null;
      turnConfigExpiresAt = 0;
      els.connectButton.disabled = false;
      els.connectButton.textContent = "Try again";
      setRegistration("error", "Sign-in failed");
      showDialog(els.accountDialog);
    });
    ua.on("newRTCSession", handleNewSession);
    ua.start();
  } catch (error) {
    connectedPassword = "";
    webRtcPcConfig = null;
    turnConfigExpiresAt = 0;
    els.accountError.textContent = error instanceof Error ? error.message : "Could not start the secure phone.";
    els.connectButton.disabled = false;
    els.connectButton.textContent = "Try again";
    setRegistration("error", "Connection error");
  }
}

function disconnectAccount(clearIdentity = true) {
  if (activeSession) {
    try { activeSession.terminate(); } catch (_) { /* session already closed */ }
  }
  if (ua) {
    try { ua.stop(); } catch (_) { /* transport already closed */ }
  }
  ua = null;
  activeSession = null;
  pendingIncomingSession = null;
  connectedPassword = "";
  webRtcPcConfig = null;
  turnConfigExpiresAt = 0;
  stopCallTone();
  resetRemoteAudio();
  resetCallControls();
  if (clearIdentity) setConnectedIdentity("");
  setRegistration("offline", "Not connected");
}

function handleNewSession(event) {
  const session = event.session;
  const incoming = event.originator === "remote";
  wireSession(session, incoming);

  if (!incoming) {
    activeSession = session;
    beginCallingView();
    return;
  }

  if (activeSession?.isEnded()) activeSession = null;
  if (dndEnabled || activeSession) {
    session.terminate({ status_code: 486, reason_phrase: "Busy Here" });
    addActivity("missed", remoteNumber(session), dndEnabled ? "Declined by DND" : "Busy");
    return;
  }

  pendingIncomingSession = session;
  const caller = session.remote_identity;
  els.incomingCaller.textContent = caller?.display_name || caller?.uri?.user || "Unknown caller";
  els.incomingNumber.textContent = caller?.uri?.user || "";
  els.callState.textContent = "Incoming call";
  els.answerButton.disabled = false;
  els.answerButton.textContent = "Answer";
  unlockAudio().then(() => startCallTone("incoming")).catch(() => {});
  showDialog(els.incomingDialog);
}

function wireSession(session, incoming) {
  session.on("icecandidate", ({ candidate, ready }) => {
    const candidateText = candidate?.candidate || "";
    const isRelay = candidate?.type === "relay" || /\btyp relay\b/i.test(candidateText);
    if (isRelay && typeof ready === "function") ready();
  });
  if (session.connection) attachPeerConnection(session, session.connection);
  session.on("peerconnection", ({ peerconnection }) => attachPeerConnection(session, peerconnection));
  session.on("progress", () => {
    activeSession = session;
    els.callState.textContent = incoming ? "Ringing" : "Calling…";
    if (!incoming) unlockAudio().then(() => startCallTone("ringback")).catch(() => {});
  });
  session.on("accepted", () => activateCall(session));
  session.on("confirmed", () => activateCall(session));
  session.on("getusermediafailed", (error) => {
    els.callState.textContent = mediaErrorMessage(error);
  });
  session.on("hold", () => {
    held = true;
    els.holdButton.classList.add("active");
    els.holdButton.setAttribute("aria-pressed", "true");
    els.callState.textContent = "Call on hold";
  });
  session.on("unhold", () => {
    held = false;
    els.holdButton.classList.remove("active");
    els.holdButton.setAttribute("aria-pressed", "false");
    els.callState.textContent = "Call connected";
  });
  session.on("ended", (event) => finishSession(session, "completed", event?.cause || "Ended"));
  session.on("failed", (event) => finishSession(session, incoming ? "missed" : "failed", event?.cause || "Failed"));
}

function remoteNumber(session) {
  return session?.remote_identity?.uri?.user || sanitizedNumber(els.dialNumber.value) || "Unknown";
}

function beginCallingView() {
  els.callButton.classList.add("hidden");
  els.hangupButton.classList.remove("hidden");
  els.dialNumber.disabled = true;
  els.callState.textContent = "Calling…";
}

function activateCall(session) {
  if (activeSession && activeSession !== session) return;
  stopCallTone();
  activeSession = session;
  attachPeerConnection(session, session.connection);
  syncRemoteAudio(session.connection);
  pendingIncomingSession = null;
  closeDialog(els.incomingDialog);
  beginCallingView();
  els.callState.textContent = "Call connected";
  [els.muteButton, els.holdButton, els.transferButton].forEach((button) => { button.disabled = false; });
  if (!callStartedAt) {
    callStartedAt = Date.now();
    startTimer();
  }
}

function finishSession(session, result, detail) {
  const wasPending = pendingIncomingSession === session;
  const wasActive = activeSession === session;
  if (pendingIncomingSession === session) {
    pendingIncomingSession = null;
    closeDialog(els.incomingDialog);
  }
  if (!wasActive) {
    if (wasPending) {
      stopCallTone();
      els.answerButton.disabled = false;
      els.answerButton.textContent = "Answer";
      addActivity(result, remoteNumber(session), detail);
      els.callState.textContent = ua?.isRegistered() ? "Ready to call" : "Not connected";
    }
    return;
  }
  stopCallTone();
  resetRemoteAudio();
  addActivity(result, remoteNumber(session), detail);
  activeSession = null;
  resetCallControls();
  els.callState.textContent = ua?.isRegistered() ? "Ready to call" : "Not connected";
}

function resetCallControls() {
  clearInterval(timerHandle);
  timerHandle = null;
  callStartedAt = null;
  els.callTimer.textContent = "00:00";
  els.callButton.classList.remove("hidden");
  els.hangupButton.classList.add("hidden");
  els.dialNumber.disabled = false;
  [els.muteButton, els.holdButton, els.transferButton].forEach((button) => {
    button.disabled = true;
    button.classList.remove("active");
    button.setAttribute("aria-pressed", "false");
  });
  muted = false;
  held = false;
  updateCallButton();
}

function startTimer() {
  clearInterval(timerHandle);
  const update = () => {
    const seconds = Math.max(0, Math.floor((Date.now() - callStartedAt) / 1000));
    const minutesPart = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secondsPart = String(seconds % 60).padStart(2, "0");
    els.callTimer.textContent = `${minutesPart}:${secondsPart}`;
  };
  update();
  timerHandle = setInterval(update, 1000);
}

async function makeCall() {
  const target = sanitizedNumber(els.dialNumber.value);
  if (!target || !ua?.isRegistered() || activeSession) return;
  els.dialNumber.value = target;
  els.callButton.disabled = true;
  els.callState.textContent = "Preparing microphone…";
  try {
    await unlockAudio();
    await checkMicrophone();
    const pcConfig = await ensureWebRtcPcConfig();
    if (!ua?.isRegistered() || activeSession) return;
    activeSession = ua.call(`sip:${target}@${tenant.realm}`, {
      mediaConstraints: { audio: true, video: false },
      pcConfig,
    });
  } catch (error) {
    activeSession = null;
    els.callState.textContent = mediaErrorMessage(error);
    resetCallControls();
  }
}

function addActivity(type, number, detail) {
  activity.unshift({ type, number, detail, time: new Date() });
  activity = activity.slice(0, 12);
  renderActivity();
}

function renderActivity() {
  els.activityList.replaceChildren();
  if (!activity.length) {
    const item = document.createElement("li");
    item.className = "empty-state";
    item.innerHTML = '<span class="empty-icon" aria-hidden="true">⌁</span><p>No calls in this browser session</p>';
    els.activityList.append(item);
    return;
  }
  activity.forEach((entry) => {
    const item = document.createElement("li");
    item.className = `activity-entry ${entry.type}`;
    const icon = entry.type === "completed" ? "↗" : entry.type === "missed" ? "↓" : "!";
    item.innerHTML = `<span class="activity-icon">${icon}</span><span><strong></strong><small></small></span><time></time>`;
    item.querySelector("strong").textContent = entry.number;
    item.querySelector("small").textContent = entry.detail;
    item.querySelector("time").textContent = entry.time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    els.activityList.append(item);
  });
}

els.accountButton.addEventListener("click", () => showDialog(els.accountDialog));
$$('[data-close-dialog]').forEach((button) => button.addEventListener("click", () => closeDialog(button.closest("dialog"))));
els.accountForm.addEventListener("submit", (event) => {
  event.preventDefault();
  unlockAudio().catch(() => {});
  const extension = sanitizedNumber(els.extensionInput.value);
  const password = els.passwordInput.value;
  if (!extension || !password) {
    els.accountError.textContent = "Enter both the extension and SIP password.";
    return;
  }
  connectAccount(extension, password);
});
els.disconnectButton.addEventListener("click", () => {
  disconnectAccount();
  els.passwordInput.value = "";
  els.connectButton.textContent = "Connect securely";
});

$$('[data-digit]').forEach((button) => button.addEventListener("click", () => {
  const digit = button.dataset.digit;
  if (activeSession?.isEstablished()) {
    activeSession.sendDTMF(digit, { duration: 160, interToneGap: 80 });
  } else {
    els.dialNumber.value += digit;
    updateCallButton();
  }
}));
els.dialNumber.addEventListener("input", () => {
  els.dialNumber.value = sanitizedNumber(els.dialNumber.value);
  updateCallButton();
});
els.dialNumber.addEventListener("keydown", (event) => {
  if (event.key === "Enter") makeCall();
});
els.backspaceButton.addEventListener("click", () => {
  els.dialNumber.value = els.dialNumber.value.slice(0, -1);
  updateCallButton();
});
els.callButton.addEventListener("click", makeCall);
els.hangupButton.addEventListener("click", () => activeSession?.terminate());

els.answerButton.addEventListener("click", async () => {
  const session = pendingIncomingSession;
  if (!session || session.isEnded()) {
    pendingIncomingSession = null;
    stopCallTone();
    closeDialog(els.incomingDialog);
    els.callState.textContent = ua?.isRegistered() ? "The caller has already disconnected" : "Not connected";
    return;
  }
  els.answerButton.disabled = true;
  els.answerButton.textContent = "Answering…";
  stopCallTone();
  try {
    await unlockAudio();
    await checkMicrophone();
    const pcConfig = await ensureWebRtcPcConfig();
    if (pendingIncomingSession !== session || session.isEnded()) {
      throw new Error("The caller disconnected before the call could be answered.");
    }
    session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig });
    activeSession = session;
    pendingIncomingSession = null;
    closeDialog(els.incomingDialog);
    beginCallingView();
    els.callState.textContent = "Answering…";
  } catch (error) {
    if (activeSession === session) activeSession = null;
    if (pendingIncomingSession === session) pendingIncomingSession = null;
    closeDialog(els.incomingDialog);
    els.answerButton.disabled = false;
    els.answerButton.textContent = "Answer";
    addActivity("missed", remoteNumber(session), mediaErrorMessage(error));
    els.callState.textContent = mediaErrorMessage(error);
    resetCallControls();
  }
});
els.rejectButton.addEventListener("click", () => {
  stopCallTone();
  pendingIncomingSession?.terminate({ status_code: 486, reason_phrase: "Declined" });
  pendingIncomingSession = null;
  closeDialog(els.incomingDialog);
});

els.muteButton.addEventListener("click", () => {
  if (!activeSession?.isEstablished()) return;
  muted = !muted;
  if (muted) activeSession.mute({ audio: true });
  else activeSession.unmute({ audio: true });
  els.muteButton.classList.toggle("active", muted);
  els.muteButton.setAttribute("aria-pressed", String(muted));
});
els.holdButton.addEventListener("click", () => {
  if (!activeSession?.isEstablished()) return;
  if (held) activeSession.unhold();
  else activeSession.hold();
});
els.transferButton.addEventListener("click", () => {
  els.transferTarget.value = "";
  els.transferError.textContent = "";
  showDialog(els.transferDialog);
});
els.transferForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const target = sanitizedNumber(els.transferTarget.value);
  if (!target || !activeSession?.isEstablished()) {
    els.transferError.textContent = "Enter a valid destination while the call is connected.";
    return;
  }
  try {
    activeSession.refer(`sip:${target}@${tenant.realm}`);
    closeDialog(els.transferDialog);
  } catch (error) {
    els.transferError.textContent = error instanceof Error ? error.message : "Transfer could not be started.";
  }
});

els.dndButton.addEventListener("click", () => {
  dndEnabled = !dndEnabled;
  els.dndButton.classList.toggle("active", dndEnabled);
  els.dndButton.setAttribute("aria-pressed", String(dndEnabled));
  if (ua?.isRegistered()) {
    els.availabilityText.textContent = dndEnabled ? "Do not disturb" : "Available";
  }
});
els.clearHistoryButton.addEventListener("click", () => {
  activity = [];
  renderActivity();
});

window.addEventListener("beforeunload", () => {
  if (ua) ua.stop();
});

initBrand();
setConnectedIdentity("");
setRegistration("offline", "Not connected");
showDialog(els.accountDialog);
